// Socket.io service for real-time notifications
// NOTE: WebSocket is currently disabled. To enable, set VITE_SOCKET_ENABLED=true in .env
import { io, Socket } from 'socket.io-client';
import { AuthService } from './authService';

class SocketService {
  private socket: Socket | null = null;
  private eventHandlers: Map<string, Function[]> = new Map();
  private isEnabled: boolean = import.meta.env.VITE_SOCKET_ENABLED === 'true';

  /**
   * Connect to Socket.io server
   * Called when Layout component mounts (user is authenticated)
   */
  connect(): void {
    // WebSocket is disabled - do nothing
    if (!this.isEnabled) {
      return;
    }

    // Prevent duplicate connections
    if (this.socket?.connected) {
      console.log('Socket already connected');
      return;
    }

    const token = AuthService.getToken();
    if (!token) {
      console.error('No auth token found, cannot connect socket');
      return;
    }

    // Determine socket URL based on environment
    const socketUrl = import.meta.env.VITE_SOCKET_URL || 'ws://localhost:3004';

    console.log('🔌 Connecting to Socket.io server:', socketUrl);

    this.socket = io(socketUrl, {
      auth: {
        token, // Send JWT token for authentication
      },
      transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    this.setupEventListeners();
  }

  /**
   * Setup built-in Socket.io event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket?.id);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 Socket reconnected after ${attemptNumber} attempts`);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('❌ Socket reconnection error:', error.message);
    });

    // Note: Socket.io automatically preserves event listeners across reconnections
    // No need to manually reattach handlers
  }

  /**
   * Listen to a specific event
   * @param event Event name (e.g., 'new_lead', 'reminder_due')
   * @param handler Callback function
   */
  on(event: string, handler: Function): void {
    // If socket is disabled, silently ignore
    if (!this.isEnabled) {
      return;
    }

    if (!this.socket) {
      console.warn('Socket not connected, storing handler for later');
    }

    // Store handler for reconnection
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)?.push(handler);

    // Attach to socket if connected
    this.socket?.on(event, handler as any);
  }

  /**
   * Remove event listener
   * @param event Event name
   * @param handler Callback function (optional, removes all if not provided)
   */
  off(event: string, handler?: Function): void {
    if (handler) {
      // Remove specific handler
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
      this.socket?.off(event, handler as any);
    } else {
      // Remove all handlers for event
      this.eventHandlers.delete(event);
      this.socket?.off(event);
    }
  }

  /**
   * Emit an event to server
   * @param event Event name
   * @param data Data to send
   */
  emit(event: string, data?: any): void {
    if (!this.socket?.connected) {
      console.error('Socket not connected, cannot emit event:', event);
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Disconnect socket
   * Called when user logs out or component unmounts
   */
  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnecting socket');
      this.socket.disconnect();
      this.socket = null;
      this.eventHandlers.clear();
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get socket ID
   */
  getSocketId(): string | undefined {
    return this.socket?.id;
  }
}

// Export singleton instance
export const socketService = new SocketService();
export default socketService;
